import { Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight } from "lucide-react";
import { Container } from "@/components/site/Bits";

const CREDENTIALS = [
  // PCAQT has no logo we are entitled to display, so it carries a wordmark instead.
  { wordmark: "PCAQT", name: "PCA Qualified Technician", note: "Damp & Timber", testId: "credential-pcaqt" },
  { logo: "/logo-chas.webp", alt: "CHAS", name: "CHAS Accredited Contractor", note: "Health & safety assessed", testId: "credential-chas" },
  { logo: "/logo-ukame.webp", alt: "UK Academy of Mould Experts", name: "UKAME Accredited", note: "Corporate member", testId: "credential-ukame" },
  { logo: "/logo-dewpoint.webp", alt: "Dewpoint Professional", name: "Dewpoint Professional", note: "Advanced Mould Remediation", testId: "credential-dewpoint" },
  // CSCS opens the second row of the grid.
  { logo: "/logo-cscs.webp", alt: "CSCS", name: "Construction Skills Certification Scheme", note: "Blue & Gold Skilled Workers", testId: "credential-cscs" },
];

// The two NVQs and the HS&E test share one box, so the trade qualifications read together.
const TRADE_QUALIFICATIONS = [
  { logo: "/badge-nvq2.webp", alt: "NVQ Level 2", name: "NVQ Level 2", note: "Damp-proofing & wood preservation", testId: "credential-nvq2" },
  { logo: "/badge-nvq3.webp", alt: "NVQ Level 3", name: "NVQ Level 3", note: "Insulation & building treatments", testId: "credential-nvq3" },
  { logo: "/badge-hse.webp", alt: "Health, Safety and Environment", name: "HS&E tested", note: "Health, safety & environment", testId: "credential-hse" },
];

export const CredentialsBand = () => (
  <section className="border-t border-pg-border bg-white py-10 md:py-24" data-testid="home-credentials">
    <Container>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.6fr)] lg:gap-16">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <span aria-hidden="true" className="h-6 w-[3px] rounded-full bg-pg-primary sm:h-10" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pg-primary sm:text-xs sm:tracking-[0.22em]">
              Accredited &amp; certified
            </p>
          </div>
          <h2 className="mt-3 text-xl sm:mt-5 sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-pg-dark">
            Qualified, accredited
            <br className="hidden sm:block" /> and accountable
          </h2>
          <p className="mt-2.5 text-sm leading-relaxed text-pg-muted sm:mt-4 md:text-base">
            Specialist qualifications in damp, timber and mould, independently assessed health and
            safety, and carded operatives on every site.
          </p>
          <div className="mt-4 flex items-start gap-2.5 border-l-2 border-pg-border pl-3 sm:mt-6 sm:gap-3 sm:pl-4"
               data-testid="home-credentials-insurance">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-pg-primary sm:h-5 sm:w-5" />
            <p className="text-[13px] font-medium leading-snug text-pg-text sm:text-sm">
              Fully insured, including professional indemnity.
            </p>
          </div>
          <Link
            to="/about#qualifications"
            data-testid="home-credentials-link"
            className="group mt-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-pg-primary sm:mt-7"
          >
            See our qualifications
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <ul className="grid grid-cols-2 border-l border-t border-pg-border sm:grid-cols-3 lg:grid-cols-4">
          {CREDENTIALS.map((item) => (
            <li
              key={item.testId}
              data-testid={item.testId}
              className={`group relative border-b border-r border-pg-border p-2.5 transition-colors duration-300 hover:bg-pg-pale sm:p-5 ${
                item.testId === "credential-cscs" ? "col-span-2 lg:col-span-1" : ""
              }`}
            >
              <span aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-[2px] origin-left scale-x-0 bg-pg-bright transition-transform duration-300 group-hover:scale-x-100" />
              <span className="flex h-9 items-center sm:h-14">
                {item.logo ? (
                  <img src={item.logo} alt={item.alt} loading="lazy"
                       className="max-h-9 w-auto max-w-[78px] object-contain object-left sm:max-h-14 sm:max-w-[130px]" />
                ) : (
                  <span className="font-display text-lg font-extrabold tracking-wide text-pg-primary sm:text-2xl">
                    {item.wordmark}
                  </span>
                )}
              </span>
              <p className="mt-2 text-[13px] font-bold leading-snug text-pg-dark sm:mt-4">{item.name}</p>
              <p className={`mt-1 text-xs leading-snug text-pg-muted sm:block sm:text-[11px] ${
                item.testId === "credential-cscs" ? "" : "hidden"
              }`}>{item.note}</p>
            </li>
          ))}

          <li
            data-testid="credential-trade-qualifications"
            className="group relative col-span-2 border-b border-r border-pg-border p-2.5 transition-colors duration-300 hover:bg-pg-pale sm:col-span-3 sm:p-5"
          >
            <span aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-[2px] origin-left scale-x-0 bg-pg-bright transition-transform duration-300 group-hover:scale-x-100" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-pg-muted">
              Trade qualifications held
            </p>
            <div className="mt-2.5 grid gap-2.5 sm:mt-4 sm:grid-cols-3 sm:gap-4">
              {TRADE_QUALIFICATIONS.map((item) => (
                <div
                  key={item.testId}
                  data-testid={item.testId}
                  className="flex items-center gap-3 sm:flex-col sm:items-start sm:gap-0"
                >
                  <span className="flex h-9 w-[46px] shrink-0 items-center sm:h-14 sm:w-auto">
                    <img src={item.logo} alt={item.alt} loading="lazy"
                         className="max-h-9 w-auto object-contain object-left sm:max-h-14" />
                  </span>
                  <span>
                    <p className="text-[13px] font-bold leading-snug text-pg-dark sm:mt-4">{item.name}</p>
                    <p className="mt-0.5 text-xs leading-snug text-pg-muted sm:mt-1 sm:text-[11px]">{item.note}</p>
                  </span>
                </div>
              ))}
            </div>
          </li>
        </ul>
      </div>
    </Container>
  </section>
);

export default CredentialsBand;
